/**
 * Headline Test Service - Serviço para gerenciamento de testes A/B de headline
 *
 * Integra com as rotas do backend em /headline-test
 */

import { api } from './api';
import { HeadlineOverlayVariation } from '@/store/videoFeatures/types/headline-overlay';

export interface CreateHeadlineTestPayload {
  headlineTestData: {
    headlineTestTitle: string;
    userId: string;
    endDate?: string;
  };
  headlineTestConfig: Array<{
    // Configurações de headline
    headlineText?: string;
    subHeadlineText?: string;
    headlineColor?: string;
    subHeadlineColor?: string;
    headlineSize?: string | number;
    subHeadlineSize?: string | number;
    headlineTypeFont?: string;
    headlineFontWeight?: string;

    // Forçar headline ativada
    haveHeadline: boolean;
  }>;
}

export interface UpdateHeadlineTestTitlePayload {
  headlineTestTitle: string;
}

export interface HeadlineTestResponse {
  id_headline_test: string;
  title: string;
  id_user: string;
  id_video: string;
  is_active: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface HeadlineTestMetrics {
  headlineTest: {
    id_video: string;
    id_headline_test: string;
    trafego: number;
    contador_trafego: number;
    nomeVideo?: string;
    frame?: string;
  };
  metric: any; // Métricas do vídeo
}

/**
 * Cria um novo teste de headline
 */
export async function createHeadlineTest(
  originalVideoId: string,
  payload: CreateHeadlineTestPayload,
  token: string
): Promise<HeadlineTestResponse> {
  const response = await api.post(`/headline-test/${originalVideoId}`, payload, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  // O api.post já retorna response.data, então response já é o objeto esperado
  return response as HeadlineTestResponse;
}

/**
 * Atualiza o título de um teste de headline
 */
export async function updateHeadlineTestTitle(
  headlineTestId: string,
  payload: UpdateHeadlineTestTitlePayload,
  token: string
): Promise<void> {
  await api.put(`/headline-test/${headlineTestId}`, payload, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

/**
 * Deleta um teste de headline
 */
export async function deleteHeadlineTest(
  headlineTestId: string,
  token: string
): Promise<void> {
  await api.delete(`/headline-test/${headlineTestId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

/**
 * Busca todos os testes de headline de um usuário
 */
export async function findUserHeadlineTests(
  userId: string,
  token: string
): Promise<HeadlineTestResponse[]> {
  const response = await api.get(`/headline-test/${userId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response as HeadlineTestResponse[];
}

/**
 * Busca métricas de um teste de headline
 */
export async function getHeadlineTestMetrics(
  headlineTestId: string,
  startDate: string,
  endDate: string,
  token: string
): Promise<HeadlineTestMetrics[]> {
  const response = await api.get(`/headline-test/${headlineTestId}/metrics`, {
    params: {
      inicio: startDate,
      fim: endDate,
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  // Backend retorna { message: string, data: HeadlineTestMetrics[] }
  return (response as any).data;
}

/**
 * Libera um vídeo do teste de headline baseado na distribuição de tráfego
 */
export async function releaseHeadlineTestVideo(
  headlineTestId: string,
  token: string
): Promise<{ data: any }> {
  const response = await api.get(`/headline-test/${headlineTestId}/videos`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response as { data: any };
}
